<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { OpenItem, OpenItemPostPayload } from "../../models/contador";
import KeyValuePair from "../KeyValuePair.vue";
import { DateTime } from "luxon";
import ProperCurrency from "../proper-inputs/proper-currency.vue";
import ProperDatePicker from "../proper-inputs/proper-date-picker.vue";
import ProperSelect from "../proper-inputs/proper-select.vue";
import { usePortalBreakpoints } from "../../hooks/usePortalBreakpoints.ts";

const { isDesktop } = usePortalBreakpoints();

const props = defineProps<{
    openItem: OpenItem | null;
    unknownPropertyId: string;
    propertyOptions: Array<{ label: string; value: string; disabled?: boolean }>;
}>();

const emits = defineEmits<{
    (e: "update:field", field: keyof OpenItemPostPayload, value: string | number | null): void;
}>();

const sourceOptions = computed(() => {
    return [
        { label: "PMS", value: "pms" },
        { label: "Bank", value: "bank" },
    ];
});

/**
 * Handles local state and emits for inputs.
 */
function useInputs() {
    const localPropertyValue = ref(props.openItem?.property_id || props.unknownPropertyId);
    const localAmountValue = ref(props.openItem?.amount || 0);
    const localDateValue = ref(
        props.openItem?.date ? DateTime.fromISO(props.openItem.date).startOf("day").toMillis() : 0,
    );
    const localSourceValue = ref(props.openItem?.source || "");

    function onPropertyInput(val: string) {
        if (val !== localPropertyValue.value) {
            localPropertyValue.value = val;
            emits("update:field", "property_id", val === props.unknownPropertyId ? null : val);
        }
    }

    watch(
        () => props.openItem?.property_id,
        (newVal) => {
            localPropertyValue.value = newVal || props.unknownPropertyId;
        },
    );

    function onAmountInput(val: number) {
        if (val !== props.openItem?.amount) {
            emits("update:field", "amount", val);
        }
    }

    watch(
        () => props.openItem?.amount,
        (newVal) => {
            localAmountValue.value = newVal ?? 0;
        },
    );

    function onDateInput(val: number) {
        if (
            val !==
            DateTime.fromISO(props.openItem?.date || "")
                .startOf("day")
                .toMillis()
        ) {
            const isoDate = DateTime.fromMillis(val)
                .startOf("day")
                .toUTC()
                .toISO({ suppressMilliseconds: true });
            emits("update:field", "date", isoDate || "");
        }
    }

    watch(
        () => props.openItem?.date,
        (newVal) => {
            localDateValue.value = newVal ? DateTime.fromISO(newVal).startOf("day").toMillis() : 0;
        },
    );

    function onSourceInput(val: string) {
        if (val !== localSourceValue.value) {
            localSourceValue.value = val;
            emits("update:field", "source", val);
        }
    }

    watch(
        () => props.openItem?.source,
        (newVal) => {
            localSourceValue.value = newVal || "";
        },
    );

    return {
        localPropertyValue,
        localAmountValue,
        localDateValue,
        localSourceValue,
        onPropertyInput,
        onAmountInput,
        onDateInput,
        onSourceInput,
    };
}
const {
    localPropertyValue,
    localAmountValue,
    localDateValue,
    localSourceValue,
    onPropertyInput,
    onAmountInput,
    onDateInput,
    onSourceInput,
} = useInputs();
</script>

<template>
    <div
        class="h-full px-10 py-6 pr-4 pb-2"
        :class="[!isDesktop ? 'overflow-hidden !px-6' : 'overflow-y-auto']"
    >
        <div :class="!isDesktop ? 'grid grid-cols-2 gap-x-6 gap-y-5' : 'space-y-5'">
            <div>
                <label class="text-label text-base text-gray-dark mb-1">PROPERTY NAME</label>
                <proper-select
                    :model-value="localPropertyValue"
                    :options="propertyOptions"
                    placeholder="Select Property *"
                    class="ml-[-13px]"
                    live-preview
                    @update:model-value="(v) => onPropertyInput(v as string)"
                />
            </div>
            <key-value-pair label="BANK" :value="openItem?.bank_account?.bank_name || '-'" />
            <key-value-pair
                label="BANK ACCOUNT"
                :value="openItem?.bank_account?.account_name || '-'"
            />
            <div>
                <label class="text-label text-base text-gray-dark mb-1">Amount</label>
                <proper-currency
                    :model-value="localAmountValue"
                    placeholder="Amount *"
                    class="ml-[-13px]"
                    live-preview
                    color-by-value
                    disable-update-on-change
                    @update:model-value="(v) => onAmountInput(v as number)"
                />
            </div>
            <div>
                <label class="text-label text-base text-gray-dark mb-1">TRANSACTION DATE</label>
                <div class="ml-[-13px]">
                    <proper-date-picker
                        :model-value="localDateValue"
                        placeholder="Transaction Date * (e.g., 01/15/2025)"
                        live-preview
                        @update:model-value="(v) => onDateInput(v as number)"
                    />
                </div>
            </div>
            <div>
                <label class="text-label text-base text-gray-dark mb-1">SOURCE</label>
                <proper-select
                    :model-value="localSourceValue"
                    :options="sourceOptions"
                    placeholder="Select Source *"
                    class="ml-[-13px]"
                    live-preview
                    @update:model-value="(v) => onSourceInput(v as string)"
                />
            </div>
            <key-value-pair
                label="ACCOUNT NUMBER"
                :value="openItem?.bank_account?.account_number || '-'"
            />
            <key-value-pair
                label="PMS ACCOUNT"
                :value="openItem?.bank_account?.account_name || '-'"
            />
        </div>
    </div>
</template>
